import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useQuery } from "@tanstack/react-query";
import { ProjectTimeSummaryPanel } from "./project-time-summary-panel";
import { ClientResources } from "./client-resources";
import { KeychainSection } from "./keychain-section";
import { useAuth } from "@/contexts/auth-context";
import { ProjectParticipants } from "./project-participants";
import { useClients } from "@/hooks/use-shared-queries";

interface ProjectDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  projectName: string;
  clientId?: string;
}

export function ProjectDetailDialog({ open, onOpenChange, projectId, projectName, clientId }: ProjectDetailDialogProps) {
  const { user } = useAuth();
  const { data: timeSummary } = useQuery<any>({
    queryKey: [`/api/time-tracking/summary/project/${projectId}`],
    enabled: open,
    staleTime: 30000,
  });

  const derivedClientId = clientId || (projectId.length >= 5 ? projectId.slice(3, 5) : undefined);

  const { data: clients } = useClients();
  const client = derivedClientId
    ? clients?.find((c) => c.id === derivedClientId)
    : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Project Details - {projectName}</DialogTitle>
          <DialogDescription>
            View comprehensive project information including time tracking summaries
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 space-y-6 pr-2">
          <div>
            <h2 className="text-lg font-semibold mb-4">Time Tracking</h2>
            <ProjectTimeSummaryPanel 
              projectId={projectId}
              timeSummary={timeSummary}
            />
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-4">Participants</h2>
            <ProjectParticipants projectId={projectId} />
          </div>

          {derivedClientId && (
            <div>
              <h2 className="text-lg font-semibold mb-1">Client Information</h2>
              {client?.companyName && (
                <p
                  className="text-sm text-muted-foreground mb-4"
                  data-testid="text-client-company-name"
                >
                  {client.companyName}
                </p>
              )}
              <ClientResources clientId={derivedClientId} />
            </div>
          )}

          {derivedClientId && (user?.role === "admin" || user?.role === "team") && (
            <div>
              <h2 className="text-lg font-semibold mb-1">Keychain</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Credentials for {client?.companyName || `client ${derivedClientId}`}
                {client?.address ? ` — ${client.address}` : ""}
              </p>
              <KeychainSection clientId={derivedClientId} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
